import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Wrench, IndianRupee, Wallet, Receipt as ReceiptIcon, Layers, Store } from "lucide-react";
import { formatBill } from "@/lib/format";
import Pagination, { usePagination } from "@/components/Pagination";

const RANGES = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Last 90 Days", "This Month", "Custom"];

function KPI({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-md overflow-hidden bg-white border border-slate-200 flex" data-testid={testid}>
      <div className="w-16 shrink-0 flex items-center justify-center" style={{ background: color }}>
        <Icon className="h-7 w-7 text-white" strokeWidth={1.8} />
      </div>
      <div className="flex-1 px-4 py-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-display font-black text-2xl tracking-tight text-slate-900 font-mono-num mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function DailyStatus() {
  const { user } = useAuth();
  const isBranch = user?.role === "branch";
  const today = new Date().toISOString().slice(0, 10);
  const [rng, setRng] = useState("Today");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/dashboard/summary", {
        params: { rng, date_from: dateFrom, date_to: dateTo, branch_id: branchId },
      });
      setData(d);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [isBranch]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rng, branchId, dateFrom, dateTo]);

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-4" data-testid="daily-status-page">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 font-display font-bold text-slate-900 tracking-tight text-sm">Filters</div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">From Date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="ds-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">To Date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="ds-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Range</Label>
            <Select value={rng} onValueChange={setRng}>
              <SelectTrigger className="h-9" data-testid="ds-range"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isBranch && (
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="h-9" data-testid="ds-branch">
                  <Store className="h-3.5 w-3.5 mr-1.5 text-slate-500" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">— All Branches —</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={load} disabled={loading} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold col-start-1 md:col-start-auto" data-testid="ds-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={Wrench} label="Service Count" value={data?.kpis.service_count ?? 0} color="#2E77E6" testid="ds-service-count" />
        <KPI icon={IndianRupee} label="Service Amount" value={inr(data?.kpis.service_amount || 0)} color="#DC3D5E" testid="ds-service-amount" />
        <KPI icon={Wallet} label="Advance Amount" value={inr(data?.kpis.advance_amount || 0)} color="#E8A93A" testid="ds-advance" />
        <KPI icon={ReceiptIcon} label="Receipt Amount" value={inr(data?.kpis.receipt_amount || 0)} color="#2AA36F" testid="ds-receipt" />
        <KPI icon={Layers} label="Total Receipt" value={inr(data?.kpis.total_receipt || 0)} color="#1EA0D2" testid="ds-total" />
      </div>

      {/* Two tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <DetailsTable
          title="Service Details"
          rows={data?.services || []}
          columns={["Sr.", "Service", "Customer", "Service Type", "Amount", "Advance", "PayType", "Cur500", "Cur200", "Cur100", "Cur50"]}
          renderRow={(r, i) => (
            <>
              <td className="px-3 py-2 text-slate-500 font-mono-num">{i + 1}</td>
              <td className="px-3 py-2 font-mono-num text-slate-800">{formatBill(r.doc_no)}<div className="text-[10px] text-slate-400">({r.doc_date})</div></td>
              <td className="px-3 py-2">{r.customer_name}<div className="text-[10px] text-slate-400 font-mono-num">({r.mobile})</div></td>
              <td className="px-3 py-2 text-slate-700">{r.service_name}</td>
              <td className="px-3 py-2 text-right font-mono-num">{inr(r.amount)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{inr(r.advance)}</td>
              <td className="px-3 py-2 text-xs text-slate-600">{r.payment_type}</td>
              <td className="px-2 py-2 text-right font-mono-num text-xs">{r.cur_500 || 0}</td>
              <td className="px-2 py-2 text-right font-mono-num text-xs">{r.cur_200 || 0}</td>
              <td className="px-2 py-2 text-right font-mono-num text-xs">{r.cur_100 || 0}</td>
              <td className="px-2 py-2 text-right font-mono-num text-xs">{r.cur_50 || 0}</td>
            </>
          )}
          testid="ds-service-details"
        />
        <DetailsTable
          title="Receipt Details"
          rows={data?.receipts || []}
          columns={["Sr.", "Receipt", "Service", "Customer", "Service Type", "PayType", "Receipt", "Balance"]}
          renderRow={(r, i) => (
            <>
              <td className="px-3 py-2 text-slate-500 font-mono-num">{i + 1}</td>
              <td className="px-3 py-2 font-mono-num text-slate-800">{formatBill(r.doc_no)}<div className="text-[10px] text-slate-400">({r.doc_date})</div></td>
              <td className="px-3 py-2 font-mono-num text-xs text-slate-500">{formatBill(r.parent_doc_no || r.application_id) || "—"}</td>
              <td className="px-3 py-2">{r.customer_name}<div className="text-[10px] text-slate-400 font-mono-num">({r.mobile})</div></td>
              <td className="px-3 py-2 text-slate-700">{r.service_name}</td>
              <td className="px-3 py-2 text-xs text-slate-600">{r.payment_type}</td>
              <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.paid_amount)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{inr(r.balance)}</td>
            </>
          )}
          testid="ds-receipt-details"
        />
      </div>
    </div>
  );
}

function DetailsTable({ title, rows, columns, renderRow, testid }) {
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, "");
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={testid}>
      <div className="px-4 py-2.5 border-b-2 border-[color:var(--brand-teal)] bg-slate-50">
        <div className="font-display font-bold text-slate-900 tracking-tight">{title}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm crm-table">
          <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
            <tr>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((r, i) => (<tr key={r.id || i}>{renderRow(r, (page - 1) * pageSize + i)}</tr>))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-center py-8 text-slate-400 text-xs">No entries</div>}
      </div>
      <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid={`${testid}-pagination`} />
    </div>
  );
}
